import { LitElement, html } from "lit-element";
import { scale, makeRangeIterator } from "../utils/utils";
import { v4 as uuidv4 } from "uuid";
const Color = require("color");
class ToneViz extends LitElement {
  static get properties() {
    return {
      name: { type: String },
      mode: { type: String },

      numNotes: { type: Number },
      noteCount: { type: Number },
      transportNoteSpeed: { type: String },
      noteFadeSpeed: { type: Number },

      backgroundColor: { type: String },
      startColor: { type: String },
      endColor: { type: String },
      noteColor: { type: String },
      opacity: { type: Number },
      noteOpacity: { type: Number },
      boxShadow: { type: String },
      mixBlendMode: { type: String },
      noteMixBlendMode: { type: String },

      origin: { type: String },
      noteWidth: { type: Number },
      noteWidthOrigin: { type: String },
      noteHeight: { type: Number },
      noteHeightOrigin: { type: String },
      lengthIteratorStep: { type: Number },

      position: { type: String },
      width: { type: String },
      height: { type: String },
      top: { type: String },
      right: { type: String },
      bottom: { type: String },
      left: { type: String },
      size: { type: Number },
      widthDivision: { type: Number },
      heightDivision: { type: Number },
      offset: { type: Number },
    };
  }

  constructor() {
    super();

    this.noteCount = 0;

    // size
    this.size = 1;

    // color
    this.backgroundColor = "transparent";
    this.startColor = "blue";
    this.endColor = "red";
    this.noteColor = null;
    this.opacity = 1;
    this.noteOpacity = 1;
    this.boxShadow = "0";
    this.mixBlendMode = "normal";
    this.noteMixBlendMode = "normal";

    // position
    this.position = "absolute";
    this.origin = "left";
    this.width = "100%";
    this.height = "100%";
    this.widthDivision = 1;
    this.heightDivision = 1;
    this.left = "0";
    this.top = "0";
    this.bottom = "0";
    this.right = "0";
    this.offset = 0;

    this.container = null;
    this.containerWidth;
    this.containerHeight;

    this.noteLengthIterator;
    this.lengthIteratorStep = 1;
  }

  handleNoteOn(e) {
    const element = document.createElement("div");
    element.id = `note${e.note.id}`;

    const { note } = e;
    note.velocity = scale(e.velocity, 0, 1, 0, 127);
    element.style = this.getNoteStyle(note);

    this.container.appendChild(element);

    if (this.numNotes) {
      this.noteCount =
        this.noteCount === this.numNotes - 1 ? 0 : this.noteCount + 1;
    }
  }

  handleNoteOff(e) {
    const element = this.shadowRoot.querySelector(`#note${e.note.id}`);

    element.style.opacity = 0;

    setTimeout(() => {
      element.remove();
    }, this.noteFadeSpeed * 1000);
  }

  getNoteColor(note) {
    if (this.noteColor !== null) {
      return this.noteColor;
    }
    let factor, color, mixedColor;
    switch (this.mode) {
      case "velocity-color":
        factor = scale(note.velocity, 0, 127, 0, 1);
        color = Color.hsl(this.startColor);
        mixedColor = color.mix(Color.hsl(this.endColor), factor);
        break;
      case "sequential-position":
        factor = scale(this.noteCount, 0, this.numNotes, 0, 1);
        color = Color.hsl(this.startColor);
        mixedColor = color.mix(Color.hsl(this.endColor), factor);
        break;
      default:
        factor = scale(note.number, this.lowNote, this.highNote, 0, 1);
        color = Color.hsl(this.startColor);
        mixedColor = color.mix(Color.hsl(this.endColor), factor);
    }

    return mixedColor;
  }

  getNoteWidth(containerDimension) {
    if (this.noteWidth) {
      return this.containerWidth * (this.noteWidth / 100);
    }
    if (this.origin === "top" || this.origin === "bottom") {
      return this.containerWidth;
    }
    return (containerDimension / this.numNotes) * this.size;
  }

  getNoteStyle(note) {
    let distanceFromOrigin, width, height;
    const containerDimension =
      this.origin === "top" || this.origin === "bottom"
        ? this.containerHeight
        : this.containerWidth;

    switch (this.mode) {
      case "velocity-position":
        distanceFromOrigin = scale(
          note.velocity,
          0,
          127,
          0,
          containerDimension
        );
        break;
      case "sequential-position":
        distanceFromOrigin = scale(
          this.noteCount,
          0,
          this.numNotes,
          0,
          containerDimension
        );
        width = this.getNoteWidth(containerDimension);
        height =
          this.origin === "top" || this.origin === "bottom"
            ? (containerDimension / this.numNotes) * this.size
            : this.containerHeight;
        break;
      default:
        distanceFromOrigin = scale(
          note.number - this.offset,
          this.lowNote,
          this.highNote,
          0,
          containerDimension
        );
        width =
          this.origin === "top" || this.origin === "bottom"
            ? this.containerWidth
            : (this.containerWidth / (this.highNote - this.lowNote)) *
              this.size;
        height =
          this.origin === "left" || this.origin === "right"
            ? this.containerHeight
            : (this.containerHeight / (this.highNote - this.lowNote)) *
              this.size;
    }

    return `
      position: absolute;
      ${this.origin}: ${distanceFromOrigin}px;
      width: ${width}px;
      height: ${height}px;
      background: ${this.getNoteColor(note)};
      opacity: ${this.noteOpacity};
      transition: opacity ${this.noteFadeSpeed ? this.noteFadeSpeed : 1}s ease;
      mix-blend-mode: ${this.noteMixBlendMode};
      text-align: center;
      // TODO: clean this up
      ${this.noteWidthOrigin ? this.noteWidthOrigin : null}: ${
      this.noteWidthOrigin ? 100 - this.noteLengthIterator.next().value : null
    }%;
    ${this.noteHeightOrigin ? this.noteHeightOrigin : null}: ${
      this.noteHeightOrigin ? 100 - this.noteLengthIterator.next().value : null
    }%
      `;
  }

  startTransport() {
    Tone.Transport.scheduleRepeat((time) => {
      Tone.Draw.schedule(() => {
        const id = uuidv4();
        //TODO: make note dynamic
        this.handleNoteOn({ velocity: 1, note: { number: 60, id } });
        setTimeout(() => this.handleNoteOff({ note: { number: 60, id } }, 100));
      }, time);
    }, this.transportNoteSpeed);
  }

  firstUpdated() {
    this.container = this.shadowRoot.querySelector("#container");
    this.containerHeight = window
      .getComputedStyle(this.container)
      .height.slice(0, -2);

    this.containerWidth = window
      .getComputedStyle(this.container)
      .width.slice(0, -2);

    this.noteLengthIterator = makeRangeIterator(
      0,
      100,
      this.lengthIteratorStep
    );

    this.startTransport();
  }

  updated() {
    // TODO: don't always do this
    this.containerHeight = window
      .getComputedStyle(this.container)
      .height.slice(0, -2);

    this.containerWidth = window
      .getComputedStyle(this.container)
      .width.slice(0, -2);
  }

  render() {
    return html`
      <style>
        #container {
          transition: width 0.5s ease, height 0.5s ease, left 0.5s ease,
            top 0.5s ease;
          margin: 0 auto;
          position: ${this.position};
          display: block;
          width: ${this.width
            ? this.width
            : `calc(100% / ${this.widthDivision})`};

          height: ${this.height
            ? this.height
            : `calc(100% / ${this.heightDivision})`};

          left: ${this.left};
          top: ${this.top};
          background: ${this.backgroundColor};
          opacity: ${this.opacity};
          mix-blend-mode: ${this.mixBlendMode};
          overflow: hidden;
        }
      </style>
      <div id="container"></div>
    `;
  }
}

customElements.define("tone-viz", ToneViz);
